import type {
  ExerciseItem,
  ExerciseMode,
  ExerciseSession,
  ExerciseSessionConfig,
  VocabItem,
  ExerciseDirection,
  TypeInModeData,
  MultipleChoiceModeData,
  MatchPairsModeData,
  ListenTypeModeData,
  ClozeModeData,
  WordBuildModeData,
} from '../../types/exercise';
import { DEFAULT_SESSION_CONFIG } from '../../types/exercise';
import {
  generateMultipleChoiceOptions,
  generateMatchPairs,
  shuffleLetters,
  generateClozeSentence,
  shuffleArray,
} from './distractor-generator';
import { selectCardsForExercise } from './card-selector';

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get the prompt and correct answer based on direction
 */
function getPromptAndAnswer(
  item: VocabItem,
  direction: ExerciseDirection
): { prompt: string; correctAnswer: string } {
  if (direction === 'primary-to-target') {
    return {
      prompt: item.primary,
      correctAnswer: item.target,
    };
  } else {
    return {
      prompt: item.target,
      correctAnswer: item.primary,
    };
  }
}

/**
 * Create a TYPE_IN exercise item
 */
function createTypeInExercise(item: VocabItem, direction: ExerciseDirection): ExerciseItem {
  const { prompt, correctAnswer } = getPromptAndAnswer(item, direction);

  const modeData: TypeInModeData = {
    type: 'TYPE_IN',
    transliteration: item.transliteration,
    phonetic: item.phonetic,
  };

  return {
    id: generateId(),
    vocabItem: item,
    mode: 'TYPE_IN',
    direction,
    prompt,
    correctAnswer,
    modeData,
  };
}

/**
 * Create a MULTIPLE_CHOICE exercise item
 */
function createMultipleChoiceExercise(
  item: VocabItem,
  vocabPool: VocabItem[],
  direction: ExerciseDirection
): ExerciseItem {
  const { prompt, correctAnswer } = getPromptAndAnswer(item, direction);
  const useTarget = direction === 'primary-to-target';
  const { options, correctIndex } = generateMultipleChoiceOptions(item, vocabPool, useTarget);

  const modeData: MultipleChoiceModeData = {
    type: 'MULTIPLE_CHOICE',
    options,
    correctIndex,
  };

  return {
    id: generateId(),
    vocabItem: item,
    mode: 'MULTIPLE_CHOICE',
    direction,
    prompt,
    correctAnswer,
    modeData,
  };
}

/**
 * Create a MATCH_PAIRS exercise item (uses multiple vocab items)
 */
function createMatchPairsExercise(items: VocabItem[], direction: ExerciseDirection): ExerciseItem {
  const { leftItems, rightItems, correctPairs } = generateMatchPairs(items, 4, direction);

  const modeData: MatchPairsModeData = {
    type: 'MATCH_PAIRS',
    leftItems,
    rightItems,
    correctPairs,
  };

  return {
    id: generateId(),
    vocabItem: items[0], // Primary item for reference
    mode: 'MATCH_PAIRS',
    direction,
    prompt: 'Spárujte slova s jejich překlady',
    correctAnswer: JSON.stringify(correctPairs),
    modeData,
  };
}

/**
 * Create a LISTEN_TYPE exercise item
 */
function createListenTypeExercise(item: VocabItem, direction: ExerciseDirection): ExerciseItem {
  const { prompt, correctAnswer } = getPromptAndAnswer(item, direction);

  // For listen exercises, we speak the prompt and expect the written form
  // If direction is primary-to-target, prompt is in primary language (Czech)
  // If direction is target-to-primary, prompt is in target language
  const languageCode = direction === 'primary-to-target' ? 'cs' : item.targetLanguage?.code || 'en';

  const modeData: ListenTypeModeData = {
    type: 'LISTEN_TYPE',
    languageCode,
    textToSpeak: prompt,
    showTextHint: false,
  };

  return {
    id: generateId(),
    vocabItem: item,
    mode: 'LISTEN_TYPE',
    direction,
    prompt: 'Napište, co slyšíte',
    correctAnswer,
    modeData,
  };
}

/**
 * Create a CLOZE_SMART exercise item
 */
function createClozeExercise(item: VocabItem, direction: ExerciseDirection): ExerciseItem | null {
  const clozeData = generateClozeSentence(item, direction);

  if (!clozeData) {
    return null; // No suitable example sentence found
  }

  const modeData: ClozeModeData = {
    type: 'CLOZE_SMART',
    sentenceWithGap: clozeData.sentenceWithGap,
    gapWord: clozeData.gapWord,
    gapPosition: clozeData.gapPosition,
  };

  return {
    id: generateId(),
    vocabItem: item,
    mode: 'CLOZE_SMART',
    direction,
    prompt: clozeData.sentenceWithGap,
    correctAnswer: clozeData.gapWord,
    modeData,
  };
}

/**
 * Create a WORD_BUILD exercise item
 */
function createWordBuildExercise(item: VocabItem, direction: ExerciseDirection): ExerciseItem {
  const { prompt, correctAnswer } = getPromptAndAnswer(item, direction);
  const shuffledLetters = shuffleLetters(correctAnswer);

  const modeData: WordBuildModeData = {
    type: 'WORD_BUILD',
    shuffledLetters,
    targetWord: correctAnswer,
  };

  return {
    id: generateId(),
    vocabItem: item,
    mode: 'WORD_BUILD',
    direction,
    prompt,
    correctAnswer,
    modeData,
  };
}

/**
 * Distribute modes based on configuration percentages
 * @param config - Session configuration
 * @param totalExercises - Total number of exercises to generate
 * @param disabledModes - Optional array of modes to exclude
 */
function distributeModes(
  config: ExerciseSessionConfig,
  totalExercises: number,
  disabledModes: ExerciseMode[] = []
): ExerciseMode[] {
  const modes: ExerciseMode[] = [];
  const distribution = config.modeDistribution;

  // Safety check: ensure modeDistribution is defined
  if (!distribution) {
    throw new Error('modeDistribution is not defined in config');
  }

  // Filter out disabled modes
  const enabledModeEntries = (Object.entries(distribution) as [ExerciseMode, number][]).filter(
    ([mode]) => !disabledModes.includes(mode)
  );

  if (enabledModeEntries.length === 0) {
    // If all modes are disabled, use TYPE_IN as fallback
    console.warn('⚠️ All exercise modes are disabled, falling back to TYPE_IN');
    return new Array(totalExercises).fill('TYPE_IN');
  }

  // Recalculate percentages for enabled modes only
  const totalPercentage = enabledModeEntries.reduce((sum, [_, pct]) => sum + pct, 0);
  const normalizedEntries = enabledModeEntries.map(([mode, pct]) => [
    mode,
    (pct / totalPercentage) * 100,
  ] as [ExerciseMode, number]);

  // Calculate count for each mode based on percentage
  let remaining = totalExercises;

  normalizedEntries.forEach(([mode, percentage], index) => {
    // Last mode gets remaining count to handle rounding
    const count =
      index === normalizedEntries.length - 1
        ? remaining
        : Math.round((percentage / 100) * totalExercises);

    for (let i = 0; i < count && modes.length < totalExercises; i++) {
      modes.push(mode);
    }
    remaining -= count;
  });

  // Shuffle modes for variety
  return shuffleArray(modes);
}

/**
 * Get direction for an exercise based on config
 */
function getDirection(config: ExerciseSessionConfig, index: number): ExerciseDirection {
  if (config.direction === 'mixed') {
    return index % 2 === 0 ? 'primary-to-target' : 'target-to-primary';
  }
  return config.direction;
}

/**
 * Generate an exercise session from vocabulary items
 */
export function generateExerciseSession(
  vocabItems: VocabItem[],
  config: Partial<ExerciseSessionConfig> = {},
  disabledModes: ExerciseMode[] = []
): ExerciseSession {
  const fullConfig: ExerciseSessionConfig = {
    ...DEFAULT_SESSION_CONFIG,
    ...config,
    // Ensure nested modeDistribution is properly merged
    modeDistribution: {
      ...DEFAULT_SESSION_CONFIG.modeDistribution,
      ...(config.modeDistribution || {}),
    },
  };

  console.log('📋 Config:', fullConfig);
  if (disabledModes.length > 0) {
    console.log('🚫 Disabled modes:', disabledModes);
  }

  // Ensure we have enough vocab items
  const totalExercises = Math.min(fullConfig.totalExercises, vocabItems.length);
  console.log(
    '📊 Total exercises to generate:',
    totalExercises,
    '(from',
    vocabItems.length,
    'items)'
  );

  // Shuffle vocab items
  const shuffledVocab = shuffleArray(vocabItems);

  // Get mode distribution (excluding disabled modes)
  const modes = distributeModes(fullConfig, totalExercises, disabledModes);
  console.log('🎯 Mode distribution:', modes);

  // Generate exercises
  const exercises: ExerciseItem[] = [];
  let vocabIndex = 0;

  for (let i = 0; i < totalExercises; i++) {
    const mode = modes[i];
    const direction = getDirection(fullConfig, i);
    const item = shuffledVocab[vocabIndex % shuffledVocab.length];

    let exercise: ExerciseItem | null = null;

    switch (mode) {
      case 'TYPE_IN':
        exercise = createTypeInExercise(item, direction);
        vocabIndex++;
        break;

      case 'MULTIPLE_CHOICE':
        exercise = createMultipleChoiceExercise(item, shuffledVocab, direction);
        vocabIndex++;
        break;

      case 'MATCH_PAIRS':
        // Match pairs needs multiple items
        const matchItems = shuffledVocab.slice(vocabIndex, vocabIndex + 4);
        if (matchItems.length >= 2) {
          exercise = createMatchPairsExercise(matchItems, direction);
          vocabIndex += matchItems.length;
        } else {
          // Fallback to TYPE_IN if not enough items
          exercise = createTypeInExercise(item, direction);
          vocabIndex++;
        }
        break;

      case 'LISTEN_TYPE':
        exercise = createListenTypeExercise(item, direction);
        vocabIndex++;
        break;

      case 'CLOZE_SMART':
        exercise = createClozeExercise(item, direction);
        if (!exercise) {
          // Fallback to TYPE_IN if no example sentence
          exercise = createTypeInExercise(item, direction);
        }
        vocabIndex++;
        break;

      case 'WORD_BUILD':
        exercise = createWordBuildExercise(item, direction);
        vocabIndex++;
        break;
    }

    if (exercise) {
      exercises.push(exercise);
    } else {
      console.warn('⚠️ Failed to create exercise for mode:', mode, 'at index', i);
    }
  }

  console.log('✅ Generated', exercises.length, 'exercises');

  return {
    id: generateId(),
    exercises,
    currentIndex: 0,
    config: fullConfig,
    results: [],
    startedAt: new Date(),
    isComplete: false,
  };
}

/**
 * Generate an exercise session with FSRS card selection support
 *
 * This async version allows for intelligent card selection using FSRS algorithm
 * when useFSRS is enabled in config. Falls back to random selection otherwise.
 *
 * @param vocabItems - Optional vocab items pool (used when useFSRS is false)
 * @param config - Session configuration including FSRS options
 * @param disabledModes - Optional array of modes to exclude
 * @returns Promise<ExerciseSession>
 */
export async function generateExerciseSessionAsync(
  vocabItems: VocabItem[] | null,
  config: Partial<ExerciseSessionConfig> = {},
  disabledModes: ExerciseMode[] = []
): Promise<ExerciseSession> {
  const fullConfig: ExerciseSessionConfig = {
    ...DEFAULT_SESSION_CONFIG,
    ...config,
    modeDistribution: {
      ...DEFAULT_SESSION_CONFIG.modeDistribution,
      ...(config.modeDistribution || {}),
    },
  };

  let selectedVocabItems: VocabItem[];

  // Use FSRS card selection if enabled
  if (fullConfig.useFSRS && fullConfig.userId) {
    console.log('🧠 Using FSRS card selection');

    selectedVocabItems = await selectCardsForExercise({
      totalCards: fullConfig.totalExercises,
      useFSRS: true,
      userId: fullConfig.userId,
      targetLanguageId: config.targetLanguageId,
      categoryIds: config.categoryIds,
    });

    console.log(`📊 FSRS selected ${selectedVocabItems.length} cards`);
  } else {
    // Use provided vocab items (random selection)
    console.log('🎲 Using random card selection');

    if (!vocabItems || vocabItems.length === 0) {
      throw new Error('Vocab items required when useFSRS is false');
    }

    selectedVocabItems = vocabItems;
  }

  // Generate session using selected cards
  return generateExerciseSession(selectedVocabItems, fullConfig, disabledModes);
}

/**
 * Create a session with only specific mode(s)
 */
export function generateSingleModeSession(
  vocabItems: VocabItem[],
  mode: ExerciseMode,
  totalExercises: number = 10
): ExerciseSession {
  return generateExerciseSession(vocabItems, {
    totalExercises,
    modeDistribution: { [mode]: 100 },
  });
}

/**
 * Calculate session summary statistics
 */
export function calculateSessionSummary(session: ExerciseSession): {
  totalExercises: number;
  correctCount: number;
  incorrectCount: number;
  skippedCount: number;
  scorePercentage: number;
  totalTimeTaken: number;
  averageTimePerExercise: number;
  modeBreakdown: Record<ExerciseMode, { correct: number; total: number }>;
} {
  const results = session.results;
  const correctCount = results.filter((r) => r.isCorrect).length;
  const skippedCount = results.filter((r) => r.skipped).length;
  const incorrectCount = results.length - correctCount - skippedCount;
  const totalTimeTaken = results.reduce((sum, r) => sum + r.timeTaken, 0) / 1000;

  // Mode breakdown
  const modeBreakdown: Record<ExerciseMode, { correct: number; total: number }> = {
    TYPE_IN: { correct: 0, total: 0 },
    MULTIPLE_CHOICE: { correct: 0, total: 0 },
    MATCH_PAIRS: { correct: 0, total: 0 },
    LISTEN_TYPE: { correct: 0, total: 0 },
    CLOZE_SMART: { correct: 0, total: 0 },
    WORD_BUILD: { correct: 0, total: 0 },
  };

  results.forEach((result) => {
    modeBreakdown[result.mode].total++;
    if (result.isCorrect) {
      modeBreakdown[result.mode].correct++;
    }
  });

  return {
    totalExercises: session.exercises.length,
    correctCount,
    incorrectCount,
    skippedCount,
    scorePercentage: results.length > 0 ? (correctCount / results.length) * 100 : 0,
    totalTimeTaken,
    averageTimePerExercise: results.length > 0 ? totalTimeTaken / results.length : 0,
    modeBreakdown,
  };
}
